import {
  IGartnerPlotsData,
  IGartnerSeriesData,
  IGartnerSeriesPoint,
  IGartnerYearValue,
} from "../../../../pages/Dashboard/Dashboard.type";
import {GARTNER_CURRENT_COMPLETED_YEAR} from "./constants";

const valueByYear = (
  points: IGartnerYearValue[] | undefined,
  year: number
): number | null => {
  const point = points?.find((item) => item.year === year);
  return point ? point.value : null;
};

export interface IGartnerLifecyclePoint {
  year: number;
  scienceHist: number | null;
  patentsHist: number | null;
  scienceCurve: number | null;
  patentsCurve: number | null;
  h_t_fact: number | null;
  h_t_forecast: number | null;
}

export interface IGartnerSeriesChartPoint {
  year: number;
  hist: number | null;
  curve: number;
}

const collectYears = (...lists: Array<IGartnerYearValue[] | undefined>): number[] =>
  Array.from(
    new Set(
      lists.flatMap((list) => list?.map((item) => item.year) ?? [])
    )
  ).sort((a, b) => a - b);

export const buildLifecycleChartData = (
  plotsData: IGartnerPlotsData
): IGartnerLifecyclePoint[] => {
  const years = collectYears(
    plotsData.h_t_curve,
    plotsData.patents_curve,
    plotsData.science_curve,
    plotsData.patents_hist_norm,
    plotsData.science_hist_norm
  );

  return years.map((year) => {
    const h_t = valueByYear(plotsData.h_t_curve, year);
    const isFact = year <= GARTNER_CURRENT_COMPLETED_YEAR;
    const isForecast = year >= GARTNER_CURRENT_COMPLETED_YEAR;
    const showHistogram = year <= GARTNER_CURRENT_COMPLETED_YEAR;

    return {
      year,
      scienceHist: showHistogram
        ? valueByYear(plotsData.science_hist_norm, year)
        : null,
      patentsHist: showHistogram
        ? valueByYear(plotsData.patents_hist_norm, year)
        : null,
      scienceCurve: valueByYear(plotsData.science_curve, year),
      patentsCurve: valueByYear(plotsData.patents_curve, year),
      h_t_fact: isFact ? h_t : null,
      h_t_forecast: isForecast ? h_t : null,
    };
  });
};

const buildSeriesChartData = (
  points: IGartnerSeriesPoint[] | undefined
): IGartnerSeriesChartPoint[] => {
  if (!points?.length) return [];

  return points.map((point) => ({
    year: point.year,
    hist:
      point.year <= GARTNER_CURRENT_COMPLETED_YEAR && point.hist > 0
        ? point.hist
        : null,
    curve: point.curve,
  }));
};

export const buildPatentsSeriesChartData = (
  seriesData?: IGartnerSeriesData | null
): IGartnerSeriesChartPoint[] =>
  buildSeriesChartData(seriesData?.patents);

export const buildScienceSeriesChartData = (
  seriesData?: IGartnerSeriesData | null
): IGartnerSeriesChartPoint[] =>
  buildSeriesChartData(seriesData?.science);
